import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Clock, AlertTriangle, CheckCircle, XCircle, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { UserPickup } from "@/types/database";

interface AdminResponseSectionProps {
  userId: string;
  onResponse?: () => void;
}

const TIME_SLOTS = [
  { value: "9:00 AM - 12:00 PM", label: "Morning (9 AM - 12 PM)" },
  { value: "12:00 PM - 3:00 PM", label: "Afternoon (12 PM - 3 PM)" },
  { value: "3:00 PM - 6:00 PM", label: "Evening (3 PM - 6 PM)" },
];

export const AdminResponseSection = ({ userId, onResponse }: AdminResponseSectionProps) => {
  const [pickup, setPickup] = useState<UserPickup | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Counter-propose dialog state
  const [counterDialogOpen, setCounterDialogOpen] = useState(false);
  const [counterDate, setCounterDate] = useState<Date | undefined>();
  const [counterTimeSlot, setCounterTimeSlot] = useState("");

  useEffect(() => {
    fetchNeedsResponsePickup();
  }, [userId]);

  const fetchNeedsResponsePickup = async () => {
    try {
      const { data, error } = await supabase
        .from("user_pickups")
        .select("*")
        .eq("user_id", userId)
        .eq("status", "needs_user_response")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setPickup(data as UserPickup | null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching pickup needing response:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAlternate = async () => {
    if (!pickup || !pickup.counter_proposed_date || !pickup.counter_proposed_time_slot) {
      toast.error("No alternate time to accept");
      return;
    }

    setActionLoading(true);
    try {
      const { error } = await supabase
        .from("user_pickups")
        .update({
          proposed_date: pickup.counter_proposed_date,
          proposed_time_slot: pickup.counter_proposed_time_slot,
          status: "confirmed",
          counter_proposed_date: null,
          counter_proposed_time_slot: null,
          denial_reason: null,
        })
        .eq("id", pickup.id);

      if (error) throw error;

      // Update linked packages
      await supabase
        .from("packages")
        .update({ status: "scheduled" })
        .eq("pickup_id", pickup.id);

      toast.success("Pickup confirmed with alternate time!");
      await fetchNeedsResponsePickup();
      onResponse?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to accept alternate");
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenCounterPropose = () => {
    setCounterDate(undefined);
    setCounterTimeSlot("");
    setCounterDialogOpen(true);
  };

  const handleSubmitCounterProposal = async () => {
    if (!pickup || !counterDate || !counterTimeSlot) {
      toast.error("Please select date and time");
      return;
    }

    setActionLoading(true);
    try {
      const { error } = await supabase
        .from("user_pickups")
        .update({
          counter_proposed_date: format(counterDate, "yyyy-MM-dd"),
          counter_proposed_time_slot: counterTimeSlot,
          status: "counter_proposed",
          denial_reason: null,
        })
        .eq("id", pickup.id);

      if (error) throw error;

      toast.success("New time proposed! Awaiting admin confirmation.");
      setCounterDialogOpen(false);
      await fetchNeedsResponsePickup();
      onResponse?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to submit proposal");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading || !pickup) {
    return null;
  }

  const hasAlternate = pickup.counter_proposed_date && pickup.counter_proposed_time_slot;

  return (
    <>
      <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 shadow-soft">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-full">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <h3 className="font-semibold text-amber-800 dark:text-amber-200 text-lg">
                  Response Needed
                </h3>
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Your requested time wasn't available. Please choose an option below.
                </p>
              </div>

              {/* Denial reason */}
              {pickup.denial_reason && (
                <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/50 border border-amber-200 dark:border-amber-700">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    Reason: {pickup.denial_reason}
                  </p>
                </div>
              )}

              {/* Your original request vs Admin's alternate */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Original Request */}
                <div className="p-3 rounded-lg bg-muted/50 border">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    Your Request (Not Available)
                  </p>
                  <div className="space-y-1 text-sm line-through opacity-60">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="h-3.5 w-3.5" />
                      {format(new Date(pickup.proposed_date), "MMM d, yyyy")}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5" />
                      {pickup.proposed_time_slot}
                    </div>
                  </div>
                </div>

                {/* Admin's Alternate */}
                {hasAlternate && (
                  <div className="p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                    <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-2">
                      Suggested Alternate
                    </p>
                    <div className="space-y-1 text-sm font-medium text-green-700 dark:text-green-300">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-3.5 w-3.5" />
                        {format(new Date(pickup.counter_proposed_date!), "MMM d, yyyy")}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5" />
                        {pickup.counter_proposed_time_slot}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                {hasAlternate && (
                  <Button
                    size="sm"
                    onClick={handleAcceptAlternate}
                    disabled={actionLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Accept Alternate
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleOpenCounterPropose}
                  disabled={actionLoading}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Propose Different Time
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Counter-propose Dialog */}
      <Dialog open={counterDialogOpen} onOpenChange={setCounterDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Propose a New Time</DialogTitle>
            <DialogDescription>
              Select a date and time that works better for you.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Preferred Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !counterDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {counterDate ? format(counterDate, "PPP") : "Select a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={counterDate}
                    onSelect={setCounterDate}
                    disabled={(date) => {
                      const tomorrow = new Date();
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      tomorrow.setHours(0, 0, 0, 0);
                      return date < tomorrow;
                    }}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Preferred Time Window</Label>
              <Select value={counterTimeSlot} onValueChange={setCounterTimeSlot}>
                <SelectTrigger>
                  <SelectValue placeholder="Select time window" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((slot) => (
                    <SelectItem key={slot.value} value={slot.value}>
                      {slot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCounterDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitCounterProposal}
              disabled={!counterDate || !counterTimeSlot || actionLoading}
            >
              {actionLoading ? "Submitting..." : "Submit Proposal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
